import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { UserMe, UserBase, UserById, UserCreatePayload, UserUpdatePayload, ContentMaker, ContentMakerDetail, ContentMakerDetailParams, ContentMakersParams, UserByType, UsersByTypeParams } from './types';
import { USER_KEYS } from '../lib';
import type { ApiResponseList } from '@/shared/api/types';
import { userApi } from '../api';

// Get current user
export const useCurrentUser = (options?: { enabled?: boolean }) => {
  return useQuery<ApiResponse<UserMe>>({
    queryKey: USER_KEYS.me(),
    queryFn: userApi.getMe,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: options?.enabled ?? true,
  });
};

// Get user list
export const useUserList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<UserBase>>({
    queryKey: USER_KEYS.list(params),
    queryFn: () => userApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

// Get user by ID
export const useUserById = (id: number) => {
  return useQuery<ApiResponse<UserById>>({
    queryKey: USER_KEYS.detail(id),
    queryFn: () => userApi.getById(id),
    enabled: !!id,
  });
};

// Create user
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UserCreatePayload) => userApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_KEYS.all });
    },
  });
};

// Update user
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UserUpdatePayload) => userApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_KEYS.all });
    },
  });
};

export const useContentMakers = (params?: ContentMakersParams) => {
  return useQuery<ApiResponse<ContentMaker[]>, Error, ContentMaker[] | undefined>({
    queryKey: USER_KEYS.contentMakers(params),
    queryFn: () => userApi.getContentMakers(params),
    select: (data) => data.result ?? [],
  });
};

export const useContentMakerById = (params: ContentMakerDetailParams) => {
  return useQuery<ApiResponseList<ContentMakerDetail>>({
    queryKey: USER_KEYS.contentMakerDetail(params),
    queryFn: () => userApi.getContentMakerById(params),
    enabled: !!params.id,
    placeholderData: keepPreviousData,
  });
};

export const useUsersByType = (params: UsersByTypeParams) => {
  return useQuery<ApiResponseList<UserByType>>({
    queryKey: USER_KEYS.usersByType(params),
    queryFn: () => userApi.getUsersByType(params),
    enabled: !!params.user_type,
    placeholderData: keepPreviousData,
  });
};

// Delete user
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => userApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_KEYS.all });
    },
  });
};
